import { createContext, use, useMemo, useState, type ReactNode } from "react";
import type { ReconciliationResult } from "@/shared/api";

interface RunContextValue {
  result: ReconciliationResult | null;
  setResult: (result: ReconciliationResult | null) => void;
}

const RunContext = createContext<RunContextValue | null>(null);

/**
 * Holds the current reconciliation for the session.
 *
 * One POST returns everything - lines, orders, receipts, flags and the
 * workbook - so the result is kept here and every step reads from it. Nothing
 * is refetched between steps, which also means nothing can disagree between
 * them.
 *
 * Deliberately in memory only: a reload loses it and the user uploads again.
 * The alternative would be persisting financial data in the browser, which is
 * a bigger decision than a convenience warrants.
 */
export function RunProvider({ children }: { children: ReactNode }) {
  const [result, setResult] = useState<ReconciliationResult | null>(null);
  const value = useMemo(() => ({ result, setResult }), [result]);

  return <RunContext value={value}>{children}</RunContext>;
}

/**
 * Reads the current reconciliation.
 *
 * @throws Error if used outside RunProvider - a missing provider is a wiring
 *   bug, and failing loudly beats silently rendering an empty page.
 */
export function useRun(): RunContextValue {
  const context = use(RunContext);
  if (!context) throw new Error("useRun must be used inside RunProvider");
  return context;
}
